import {
  useDeleteUpSellAndCrossSell,
  useGetUpSellAndCrossSells
} from '@/apis/business/setting/integrations/StreamOrderSettings/hook';
import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import Search from '@/components/Common/Search';
import { Box, IconButton, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, useNavigate, useParams } from 'react-router-dom';

export default function UpSellsAndCrossSells() {
  const [searchText, setSearchText] = useState<string | null>(null);
  const navigate = useNavigate();
  const { integrationId } = useParams();

  const { data: UpSellAndCrossSellsData, isLoading } = useGetUpSellAndCrossSells(Number(integrationId), {
    search: searchText,
    per_page: 9999
  });
  const { mutateAsync: deleteUpSellAndCrossSell } = useDeleteUpSellAndCrossSell(Number(integrationId));

  const handleDelete = (id: number) => {
    deleteUpSellAndCrossSell(id);
  };

  return (
    <>
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontSize={16} fontWeight={500}>
          Upsell & Cross-sell Configurations
        </Typography>

        <Box display='flex' gap={2}>
          <Search
            size='small'
            placeholder='Search'
            searchText={searchText}
            setSearchText={setSearchText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                minHeight: 36
              }
            }}
          />

          <Button variant='contained' startIcon={<GoPlus />} onClick={() => navigate('upsell-and-cross-sell/add')}>
            Add
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <DataTable data={UpSellAndCrossSellsData?.data} columns={columns(handleDelete)} isLoading={isLoading} />
    </>
  );
}

const columns = (deleteRow: (id: number) => void): ColumnDef<any>[] => [
  {
    header: 'MENU',
    accessorKey: 'pos_menu.name'
  },
  {
    header: 'ITEM',
    accessorKey: 'selected_menu_items',
    cell: ({ row }) =>
      row.original.selected_menu_items?.length
        ? row.original.selected_menu_items.map((i: { name: string }) => i.name).join(', ')
        : '-'
  },
  {
    header: 'UPSELLS',
    accessorKey: 'upsell',
    cell: ({ row }) =>
      row.original.pos_modifiers?.length ? row.original.pos_modifiers.map((m: any) => m.name).join(', ') : '-'
  },
  {
    header: 'CROSS-SELLS',
    accessorKey: 'cross_sell',
    cell: ({ row }) =>
      row.original.pos_menu_items?.length ? row.original.pos_menu_items.map((i: any) => i.name).join(', ') : '-'
  },
  {
    header: 'ACTION',
    cell: (info: any) => (
      <Box display='flex' gap={1}>
        <Link to={`upsell-and-cross-sell/${info.row.original.id}`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>

        <IconButton color='error' onClick={() => deleteRow(info.row.original.id)}>
          <RiDeleteBin6Line />
        </IconButton>
      </Box>
    )
  }
];
